import { Box, Text, VStack } from "@chakra-ui/react"
import { DarkMode } from "@pathwright/ux/src/components/chakra/ui/color-mode"
import { useMemo, useState } from "react"
import {
  PackagesForAdminQuery,
  usePackagesForAdminQuery
} from "@pathwright/graphql-client/src/generated"
import EmbedCodeGenerator, {
  EmbedFieldConfig,
  EmbedFieldOption
} from "../embed/EmbedCodeGenerator"
import { getPackageEmbedIframeCode } from "../package/utils"
import { usePathwrightContext } from "../pathwright/PathwrightContext"
import { getSchoolUrl } from "../utils/urls"
import PWAdminCmdKItem from "./PWAdminCmdKItem"

type PackageForAdmin = NonNullable<
  NonNullable<PackagesForAdminQuery["packages"]>[number]
>
type PackageAssignmentRule = NonNullable<
  NonNullable<PackageForAdmin["assignment_rules"]>[number]
>

type PackageEmbedFormValues = {
  assignmentRuleId: string
  variant: "card" | "tag"
  buttonLabel: string
  buttonLink: string
  buttonTarget: "same_tab" | "new_tab"
  colorMode: "light" | "dark"
  primaryColor: string
  backgroundColor: string
  width: string
  height: string
}

const getDefaultEmbedBaseUrl = () =>
  typeof window !== "undefined" ? getSchoolUrl() : ""

const getDefaultButtonLink = (): string => {
  const baseUrl = getDefaultEmbedBaseUrl()
  if (!baseUrl) return ""

  try {
    return new URL("/sign-up/", baseUrl).toString()
  } catch (error) {
    return `${baseUrl.replace(/\/+$/, "")}/signup/`
  }
}

const parsePositiveInt = (value?: string): number | undefined => {
  if (!value) return undefined
  const parsed = parseInt(value, 10)
  return Number.isFinite(parsed) && parsed > 0 ? parsed : undefined
}

const getAssignmentRules = (pkg: PackageForAdmin): PackageAssignmentRule[] =>
  (pkg.assignment_rules || []).filter(
    (rule): rule is PackageAssignmentRule =>
      !!rule && typeof rule.id === "number"
  )

const getAssignmentTypeLabel = (assignmentType?: string | null): string => {
  switch (assignmentType) {
    case "explicit":
      return "Explicit key"
    case "store":
      return "Store"
    case "rewardful":
      return "Rewardful"
    case "utm":
      return "UTM"
    default:
      return "Rule"
  }
}

const getAssignmentRuleSummary = (rule: PackageAssignmentRule): string => {
  switch (rule.assignment_type) {
    case "explicit":
      return rule.explicit_key ? `pkg=${rule.explicit_key}` : "No pkg key"
    case "rewardful":
      return rule.rewardful_referral_id
        ? `via=${rule.rewardful_referral_id}`
        : "No referral ID"
    case "utm": {
      const parts = [
        rule.utm_source ? `source=${rule.utm_source}` : "",
        rule.utm_medium ? `medium=${rule.utm_medium}` : "",
        rule.utm_campaign ? `campaign=${rule.utm_campaign}` : ""
      ].filter(Boolean)
      return parts.length ? parts.join(", ") : "No UTM values"
    }
    case "store":
      return rule.store_id ? `store=${rule.store_id}` : "Store context"
    default:
      return `Rule ${rule.id}`
  }
}

const getAssignmentRuleOptions = (
  assignmentRules: PackageAssignmentRule[]
): EmbedFieldOption[] =>
  assignmentRules.map((rule) => ({
    value: String(rule.id),
    label: `${getAssignmentTypeLabel(
      rule.assignment_type
    )} - ${getAssignmentRuleSummary(rule)}`
  }))

const getPackageParamsForRule = (
  assignmentRule?: PackageAssignmentRule | null
): URLSearchParams => {
  const params = new URLSearchParams()

  if (!assignmentRule) {
    return params
  }

  switch (assignmentRule.assignment_type) {
    case "explicit":
      if (assignmentRule.explicit_key) {
        params.set("pkg", assignmentRule.explicit_key)
      }
      break
    case "rewardful":
      if (assignmentRule.rewardful_referral_id) {
        params.set("via", assignmentRule.rewardful_referral_id)
      }
      break
    case "utm":
      if (assignmentRule.utm_source) {
        params.set("utm_source", assignmentRule.utm_source)
      }
      if (assignmentRule.utm_medium) {
        params.set("utm_medium", assignmentRule.utm_medium)
      }
      if (assignmentRule.utm_campaign) {
        params.set("utm_campaign", assignmentRule.utm_campaign)
      }
      break
  }

  return params
}

const getPackageEmbedFields = (
  assignmentRuleOptions: EmbedFieldOption[]
): EmbedFieldConfig<PackageEmbedFormValues>[] => [
  {
    name: "assignmentRuleId",
    label: "Assignment rule",
    type: "select",
    colSpan: 2,
    options: assignmentRuleOptions,
    helpText: "Assignment rules are read-only and pulled from this package."
  },
  {
    name: "variant",
    label: "Variant",
    type: "select",
    options: [
      { label: "Card", value: "card" },
      { label: "Tag", value: "tag" },
      { label: "Badge", value: "badge" }
    ]
  },
  {
    name: "buttonLabel",
    label: "Button label",
    type: "text",
    placeholder: "Try for Free"
  },
  {
    name: "buttonLink",
    label: "Button link URL",
    type: "text",
    colSpan: 2,
    helpText:
      "Missing pkg, utm, and via params are automatically carried over from the embed URL.",
    placeholder: "https://grow.pathwright.com/sign-up/"
  },
  {
    name: "buttonTarget",
    label: "Button target",
    type: "radio",
    options: [
      { label: "Same tab", value: "same_tab" },
      { label: "New tab", value: "new_tab" }
    ]
  },
  {
    name: "colorMode",
    label: "Color mode",
    type: "radio",
    options: [
      { label: "Dark", value: "dark" },
      { label: "Light", value: "light" },
    ]
  },
  {
    name: "primaryColor",
    label: "Primary color (hex, no #)",
    type: "text",
    placeholder: "0097dc"
  },
  {
    name: "backgroundColor",
    label: "Background color (hex, no #)",
    type: "text",
    placeholder: "333132"
  },

  {
    name: "width",
    label: "Iframe width",
    type: "text",
    placeholder: "100%"
  },
  {
    name: "height",
    label: "Iframe height",
    type: "text",
    placeholder: "420"
  }
]

const getEmbedFormValues = (
  assignmentRuleId: string
): PackageEmbedFormValues => ({
  assignmentRuleId,
  variant: "card",
  buttonLabel: "Try for Free",
  buttonLink: getDefaultButtonLink(),
  buttonTarget: "same_tab",
  colorMode: "dark",
  primaryColor: "",
  backgroundColor: "",
  width: "100%",
  height: "420"
})

function PWAdminCmdKPackages() {
  const { me } = usePathwrightContext()
  const hasStaffAccess = !!me?.permissions?.has_pathwright_staff_access

  const { data, loading, error } = usePackagesForAdminQuery({
    skip: !hasStaffAccess
  })

  const packages = useMemo(() => {
    const resolvedPackages = (data?.packages || []).filter(
      (pkg): pkg is PackageForAdmin & { id: number } =>
        !!pkg && typeof pkg.id === "number"
    )

    return resolvedPackages.sort((a, b) => {
      const labelA = (a.display_name || a.name || "").toLowerCase()
      const labelB = (b.display_name || b.name || "").toLowerCase()
      return labelA.localeCompare(labelB)
    })
  }, [data?.packages])

  const [selectedPackageId, setSelectedPackageId] = useState<number | null>(
    null
  )

  if (!hasStaffAccess) {
    return null
  }

  return (
    <VStack w="100%" alignItems="stretch" gap="3px" pt="10px">
      <Text
        px={2}
        pt={2}
        pb={1}
        fontSize="xs"
        fontWeight="semibold"
        color="whiteAlpha.600"
        textTransform="uppercase"
        letterSpacing="wider"
      >
        Package embeds
      </Text>

      {loading ? (
        <Box px={2} py={1} fontSize=".8rem" color="whiteAlpha.500">
          Loading packages…
        </Box>
      ) : error ? (
        <Box px={2} py={1} fontSize=".8rem" color="red.300">
          Could not load packages.
        </Box>
      ) : packages.length ? (
        packages.map((pkg) => {
          const packageLabel =
            pkg.display_name || pkg.name || `Package ${pkg.id}`
          const isExpanded = selectedPackageId === pkg.id
          const assignmentRules = getAssignmentRules(pkg)
          const assignmentRuleOptions =
            getAssignmentRuleOptions(assignmentRules)
          const defaultAssignmentRuleId = assignmentRuleOptions[0]?.value || ""
          const hasStoreRule = assignmentRules.some(
            (rule) => rule.assignment_type === "store"
          )

          return (
            <Box key={pkg.id}>
              <PWAdminCmdKItem
                option={{
                  label: packageLabel,
                  icon: "sparkles",
                  active: isExpanded,
                  onSelect: () =>
                    setSelectedPackageId((current) =>
                      current === pkg.id ? null : pkg.id
                    )
                }}
              />

              {isExpanded && (
                <DarkMode>
                  <Box
                    mt={1}
                    mb={2}
                    mx={2}
                    p={3}
                    borderRadius="lg"
                    borderWidth="1px"
                    borderColor="whiteAlpha.200"
                    bg="whiteAlpha.100"
                    css={{
                      "& input, & select, & textarea": {
                        color: "whiteAlpha.700"
                      }
                    }}
                  >
                    <Text
                      fontSize="sm"
                      fontWeight="semibold"
                      color="whiteAlpha.800"
                      mb={2}
                      mt={0}
                    >
                      Embed for {pkg.display_name || pkg.name}
                    </Text>

                    {assignmentRules.length ? (
                      <>
                        <EmbedCodeGenerator
                          initialValues={getEmbedFormValues(
                            defaultAssignmentRuleId
                          )}
                          fields={getPackageEmbedFields(assignmentRuleOptions)}
                          buildCode={(values) => {
                            const selectedRule =
                              assignmentRules.find(
                                (rule) =>
                                  String(rule.id) === values.assignmentRuleId
                              ) || assignmentRules[0]

                            return getPackageEmbedIframeCode({
                              baseUrl: getDefaultEmbedBaseUrl() || undefined,
                              packageParams:
                                getPackageParamsForRule(selectedRule),
                              variant: values.variant,
                              buttonLabel:
                                values.buttonLabel.trim() || undefined,
                              buttonLink: values.buttonLink.trim() || undefined,
                              buttonTarget: values.buttonTarget,
                              colorMode: values.colorMode,
                              primaryColor:
                                values.primaryColor.trim() || undefined,
                              backgroundColor:
                                values.backgroundColor.trim() || undefined,
                              width: values.width.trim() || undefined,
                              height:
                                parsePositiveInt(values.height) || undefined
                            })
                          }}
                          copySuccessTitle="Copied package embed code"
                        />
                        {hasStoreRule ? (
                          <Text mt={2} fontSize="xs" color="whiteAlpha.500">
                            Store assignment rules use checkout context and may
                            not add URL params.
                          </Text>
                        ) : null}
                      </>
                    ) : (
                      <Text fontSize="xs" color="whiteAlpha.500">
                        No active assignment rules found for this package.
                      </Text>
                    )}
                  </Box>
                </DarkMode>
              )}
            </Box>
          )
        })
      ) : (
        <Box px={2} py={1} fontSize=".8rem" color="whiteAlpha.500">
          No packages found for this school.
        </Box>
      )}
    </VStack>
  )
}

export default PWAdminCmdKPackages
